import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CheckIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon,
  LinkIcon,
  ShieldCheckIcon,
  TrashIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import type { AdminUser, Locale, SessionLink, UserRole } from '@xphoto/shared';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n';
import { interpolate } from '../gallery/format';
import { formatDateTime } from '../viewer/format';
import { Portal } from '../components/Portal';
import { ConfirmModal } from '../taxonomy/ConfirmModal';
import { fetchUsers } from './admin-api';
import {
  createSessionLink,
  fetchSessionLinks,
  isTargetInvalid,
  revokeSessionLink,
} from './session-link-api';

/**
 * Section « LIENS D'ACCÈS » communautaires (Incrément A2) — console SUPERADMIN
 * (`/superadmin/liens`).
 *
 * Un lien d'accès ouvre `https://…/?access=<jeton>` = une VRAIE session pour un compte
 * PRÉ-EXISTANT (lecteur read-only OU contributeur write), sans mot de passe. Le
 * superadmin crée / liste / révoque ces liens ; ils ne sont JAMAIS partagés vers un
 * compte superadmin.
 *
 * Rendue comme les autres sections admin (`role="tabpanel"`, `<h3>`) dans le corps
 * défilant fourni par `AdminPanel` — pas d'en-tête/close/scroll propres.
 */

/** Clé i18n du libellé de rôle (réutilise la section `auth`). */
const ROLE_LABEL: Record<UserRole, MessageKey> = {
  superadmin: 'auth.roleSuperadmin',
  admin: 'auth.roleAdmin',
  user: 'auth.roleUser',
};

/**
 * Une cible est en ÉCRITURE si elle possède une bibliothèque (propriétaire) OU porte
 * le rôle `admin` → le lien donnerait l'accès en écriture (import, suppression) à
 * quiconque le détient. Les lecteurs (grantee `user` sans bibliothèque possédée) sont
 * en lecture seule.
 */
function isWriteTarget(user: AdminUser): boolean {
  return user.libraryId !== null || user.role === 'admin';
}

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

export function SessionLinksSection(): React.JSX.Element {
  const { t, locale } = useI18n();

  const [status, setStatus] = useState<LoadStatus>('idle');
  const [links, setLinks] = useState<SessionLink[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  // Comptes cibles (superadmin exclus) pour le sélecteur.
  const [users, setUsers] = useState<AdminUser[]>([]);

  // Formulaire de création.
  const [userId, setUserId] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  // URL fraîchement créée (affichée UNE SEULE FOIS) + si la cible est en écriture.
  const [fresh, setFresh] = useState<{ url: string; isWrite: boolean } | null>(null);
  const [copied, setCopied] = useState(false);

  // Chargement des liens.
  useEffect(() => {
    const controller = new AbortController();
    setStatus('loading');
    fetchSessionLinks(controller.signal)
      .then((list) => {
        if (controller.signal.aborted) return;
        setLinks(list);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setStatus('error');
        void err;
      });
    return () => controller.abort();
  }, [reloadKey]);

  // Alimente le sélecteur de cible (comptes non-superadmin, best-effort).
  useEffect(() => {
    const controller = new AbortController();
    fetchUsers(controller.signal)
      .then((list) => {
        if (!controller.signal.aborted) setUsers(list.filter((u) => u.role !== 'superadmin'));
      })
      .catch(() => {
        // Non bloquant : sans liste, le sélecteur reste vide (message d'état).
      });
    return () => controller.abort();
  }, []);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  const selectedUser = useMemo(() => users.find((u) => u.id === userId) ?? null, [users, userId]);
  const selectedIsWrite = selectedUser !== null && isWriteTarget(selectedUser);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (busy) return;
    setCreateError(null);
    setFresh(null);
    setCopied(false);
    if (userId === '') {
      setCreateError(t('sessionLinks.errorNoTarget'));
      return;
    }
    const trimmed = label.trim();
    if (trimmed.length === 0) {
      setCreateError(t('sessionLinks.errorLabel'));
      return;
    }
    setBusy(true);
    try {
      const created = await createSessionLink(userId, trimmed);
      const url = `${window.location.origin}/?access=${created.secret}`;
      setFresh({ url, isWrite: created.link.targetRole === 'admin' || selectedIsWrite });
      setLabel('');
      setUserId('');
      reload();
    } catch (err) {
      setCreateError(
        isTargetInvalid(err) ? t('sessionLinks.errorTarget') : t('sessionLinks.errorGeneric'),
      );
    } finally {
      setBusy(false);
    }
  };

  const copyUrl = async (): Promise<void> => {
    if (fresh === null) return;
    try {
      await navigator.clipboard.writeText(fresh.url);
      setCopied(true);
    } catch {
      // Presse-papiers indisponible : l'utilisateur peut sélectionner le champ.
    }
  };

  const onRevoked = useCallback(
    (id: string) => {
      setLinks((prev) =>
        prev.map((l) => (l.id === id ? { ...l, revokedAt: new Date().toISOString() } : l)),
      );
      reload();
    },
    [reload],
  );

  const errId = 'session-link-error';
  return (
    <section
      id="admin-session-links"
      role="tabpanel"
      aria-labelledby="admin-session-links-heading"
      className="flex flex-col gap-6"
    >
      <div className="flex flex-col gap-1">
        <h3
          id="admin-session-links-heading"
          className="flex items-center gap-2 text-base font-semibold text-fg"
        >
          <LinkIcon className="h-5 w-5" aria-hidden="true" />
          {t('sessionLinks.title')}
        </h3>
        <p className="text-sm text-muted">{t('sessionLinks.intro')}</p>
      </div>

      {/* Création d'un lien. */}
      <form
        onSubmit={(e) => void submit(e)}
        className="flex flex-col gap-4 rounded-lg border border-border p-4"
      >
        <h4 className="text-sm font-semibold text-fg">{t('sessionLinks.createTitle')}</h4>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1">
            <label htmlFor="sl-target" className="text-sm font-medium text-fg">
              {t('sessionLinks.targetLabel')}
            </label>
            <select
              id="sl-target"
              required
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              aria-invalid={createError !== null}
              aria-describedby={createError ? errId : undefined}
              className={inputClass}
            >
              <option value="">{t('sessionLinks.chooseTarget')}</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.email} — {t(ROLE_LABEL[u.role])}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="sl-label" className="text-sm font-medium text-fg">
              {t('sessionLinks.labelLabel')}
            </label>
            <input
              id="sl-label"
              type="text"
              value={label}
              maxLength={120}
              placeholder={t('sessionLinks.labelPlaceholder')}
              onChange={(e) => setLabel(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

        {/* Avertissement ÉCRITURE (icône + texte, jamais la couleur seule). */}
        {selectedIsWrite && (
          <p className="inline-flex items-start gap-2 rounded-md border border-warning bg-warning/10 px-3 py-2 text-sm text-fg">
            <ExclamationTriangleIcon
              className="mt-0.5 h-5 w-5 shrink-0 text-warning-text"
              aria-hidden="true"
            />
            {t('sessionLinks.writeWarning')}
          </p>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="submit"
            disabled={busy}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          >
            <LinkIcon className="h-5 w-5" aria-hidden="true" />
            {busy ? t('sessionLinks.creating') : t('sessionLinks.create')}
          </button>
          {createError && (
            <p
              id={errId}
              role="alert"
              className="inline-flex items-center gap-1 text-sm text-danger"
            >
              <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
              {createError}
            </p>
          )}
        </div>
      </form>

      {/* URL fraîchement créée (affichée UNE SEULE FOIS). */}
      {fresh !== null && (
        <div
          role="status"
          className="flex flex-col gap-2 rounded-lg border border-warning bg-warning/10 p-4"
        >
          <p className="inline-flex items-center gap-2 text-sm font-medium text-fg">
            <ExclamationTriangleIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
            {t('sessionLinks.urlWarning')}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <code className="flex-1 break-all rounded-md border border-border bg-surface px-3 py-2 text-sm text-fg">
              {fresh.url}
            </code>
            <button
              type="button"
              onClick={() => void copyUrl()}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
            >
              {copied ? (
                <CheckIcon className="h-5 w-5" aria-hidden="true" />
              ) : (
                <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
              )}
              {copied ? t('sessionLinks.copied') : t('sessionLinks.copy')}
            </button>
          </div>
          {fresh.isWrite && (
            <p className="inline-flex items-start gap-2 text-sm text-fg">
              <ExclamationTriangleIcon
                className="mt-0.5 h-5 w-5 shrink-0 text-warning-text"
                aria-hidden="true"
              />
              {t('sessionLinks.writeWarning')}
            </p>
          )}
        </div>
      )}

      {/* Liste des liens. */}
      {status === 'error' ? (
        <LoadError onRetry={reload} />
      ) : status === 'ready' ? (
        <LinksList links={links} locale={locale} onRevoked={onRevoked} />
      ) : (
        <p role="status" className="py-8 text-center text-muted">
          {t('sessionLinks.loading')}
        </p>
      )}
    </section>
  );
}

function LinksList({
  links,
  locale,
  onRevoked,
}: {
  links: readonly SessionLink[];
  locale: Locale;
  onRevoked: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  if (links.length === 0) {
    return <p className="py-6 text-center text-muted">{t('sessionLinks.empty')}</p>;
  }
  return (
    <ul className="flex flex-col gap-2">
      {links.map((link) => (
        <LinkRow key={link.id} link={link} locale={locale} onRevoked={onRevoked} />
      ))}
    </ul>
  );
}

function LinkRow({
  link,
  locale,
  onRevoked,
}: {
  link: SessionLink;
  locale: Locale;
  onRevoked: (id: string) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isRevoked = link.revokedAt !== null;
  const created = formatDateTime(link.createdAt, locale) ?? link.createdAt;
  const lastUsed =
    link.lastUsedAt !== null ? (formatDateTime(link.lastUsedAt, locale) ?? link.lastUsedAt) : null;

  const onRevoke = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setConfirmOpen(false);
    try {
      await revokeSessionLink(link.id);
      onRevoked(link.id);
    } catch {
      // Échec silencieux : le rechargement reflète l'état réel.
      onRevoked(link.id);
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3">
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="truncate text-sm font-medium text-fg">{link.label}</span>
        <span className="text-xs text-muted">
          <span className="break-all">{link.targetEmail}</span> — {t(ROLE_LABEL[link.targetRole])}
        </span>
        <span className="text-xs text-muted">
          {interpolate(t('sessionLinks.created'), { date: created })}
          {' · '}
          {lastUsed !== null
            ? interpolate(t('sessionLinks.lastUsed'), { date: lastUsed })
            : t('sessionLinks.neverUsed')}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {/* État : actif / révoqué (icône + texte, jamais la couleur seule). */}
        {isRevoked ? (
          <span className="inline-flex items-center gap-1 text-xs text-muted">
            <XCircleIcon className="h-4 w-4 shrink-0" aria-hidden="true" />
            {t('sessionLinks.revoked')}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs text-fg">
            <ShieldCheckIcon className="h-4 w-4 shrink-0 text-brand" aria-hidden="true" />
            {t('sessionLinks.active')}
          </span>
        )}
        {!isRevoked && (
          <button
            type="button"
            disabled={busy}
            onClick={() => setConfirmOpen(true)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm text-danger hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger disabled:opacity-50"
          >
            <TrashIcon className="h-4 w-4" aria-hidden="true" />
            {t('sessionLinks.revoke')}
          </button>
        )}
      </div>

      {confirmOpen && (
        <Portal>
          <ConfirmModal
            title={t('sessionLinks.confirmRevokeTitle')}
            confirmLabel={t('sessionLinks.revoke')}
            cancelLabel={t('sessionLinks.cancel')}
            busyLabel={t('sessionLinks.revoking')}
            busy={busy}
            danger
            onConfirm={() => void onRevoke()}
            onClose={() => setConfirmOpen(false)}
          >
            <p>{interpolate(t('sessionLinks.confirmRevokeBody'), { label: link.label })}</p>
          </ConfirmModal>
        </Portal>
      )}
    </li>
  );
}

const inputClass =
  'h-11 rounded-md border border-border-strong bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/** État d'erreur de chargement (icône + texte + réessai). */
function LoadError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ExclamationTriangleIcon className="h-8 w-8 text-danger" aria-hidden="true" />
      <p className="text-fg">{t('sessionLinks.errorLoad')}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('sessionLinks.retry')}
      </button>
    </div>
  );
}
